import { OwcTypography, OwcProgressBar, OwcInput, OwcCheckbox } from '@one/react';
import Banner from '../../../../components/Common/Banner';
import styles from './HardDeleteReleaseModal.module.css';

/**
 * Step 1 of the hard-delete flow: collect the deletion reason (required, min 3),
 * choose whether to also permanently delete entity items, and run the dry-run.
 * "Deleted By" is derived from the authenticated user's token (submitted as
 * `deletedBy`) and is mandatory; if the session has no valid email the flow is
 * blocked.
 *
 * Presentational: all state and handlers come from the orchestrator via props.
 */
export default function HardDeleteForm({
  reason,
  onReasonChange,
  deleteEntities,
  onDeleteEntitiesChange,
  emailValid,
  errorMessage,
  isLoading,
  onSubmit,
}) {
  return (
    <div className={styles.formStep}>
      <OwcTypography className={styles.intro}>
        Hard delete <strong>permanently removes the latest release</strong> so the same release
        version can be recreated. Provide a reason and run a <strong>dry-run</strong> to preview the
        impact before executing. No changes are made during the dry-run.
      </OwcTypography>

      <Banner variant="error">
        <strong>This is a destructive, irreversible action.</strong> Once executed, the release
        cannot be recovered. The dry-run below is safe — nothing is deleted until you confirm and
        execute.
      </Banner>

      {!emailValid && (
        <Banner variant="error">
          Could not determine your account email from the session. Deletion is unavailable — please
          re-authenticate and try again.
        </Banner>
      )}

      <form onSubmit={onSubmit}>
        <div className={styles.fieldGroup}>
          <div>
            <OwcTypography className={styles.fieldLabel}>
              Deletion reason <span className={styles.required}>*</span>
            </OwcTypography>
            <OwcInput
              type="textarea"
              variant="outlined"
              rows={4}
              maxLength={1000}
              show-char-count
              resizable
              placeholder="Reason for deletion (e.g. Release created by mistake)"
              value={reason}
              onInput={(e) => onReasonChange(e.target.value)}
              no-clear-icon
              style={{ width: '100%' }}
            />
            <OwcTypography variant="caption" className={styles.fieldHint}>
              Required, minimum 3 characters. Recorded in the audit trail.
            </OwcTypography>
          </div>

          <div className={styles.scopeBox}>
            <OwcCheckbox
              checked={deleteEntities}
              onValueChange={(e) => onDeleteEntitiesChange(e.detail)}
            >
              <span className={styles.scopeLabel}>Also permanently delete entity items</span>
            </OwcCheckbox>
            <OwcTypography variant="caption" className={styles.scopeHint}>
              {deleteEntities
                ? 'Only entities exclusive to this release will be permanently deleted. Shared entities remain active and are untagged from this release.'
                : 'Entities will be untagged only (the release tag is removed) and stay active and available.'}
            </OwcTypography>
          </div>
        </div>
      </form>

      {errorMessage && <Banner variant="error">{errorMessage}</Banner>}

      {isLoading && (
        <div className={styles.progress}>
          <OwcProgressBar size="medium" color="info" indeterminate />
          <div className={styles.progressLabel}>Analyzing impact...</div>
        </div>
      )}
    </div>
  );
}
